import { Component, DestroyRef, ElementRef, inject, NgZone, OnInit, ViewChild } from '@angular/core';
import { AsyncPipe, CommonModule, DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatNoDataRow,
  MatRecycleRows,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTab, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, catchError, EMPTY, map, of, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlertsService } from './services/alerts.service';
import {
  Alert,
  AlertFormData,
  AlertLevel,
  AlertSearchFilter,
  AlertSearchPayload,
  AlertSearchResponse,
  AlertStatus
} from './models/alerts';
import {
  ALERT_LEVEL_META,
  ALERT_STATUS_META,
  ALERT_STATUSES,
  AlertLevelMeta,
  AlertStatusMeta
} from './models/alert-level.util';
import { AlertFiltersComponent } from './components/alert-filters/alert-filters.component';
import { AlertFormComponent, AlertFormTarget } from './components/alert-form/alert-form.component';
import { AlertPreviewComponent } from './components/alert-preview/alert-preview.component';
import { AlertPreviewStackComponent } from '../../../../components/alerts/components/alert-preview-stack/alert-preview-stack.component';
import { LiveAlertsService } from '../../../../components/alerts/services/live-alerts.service';
import { AlertCardComponent } from './components/alert-card/alert-card.component';
import { ALERT_ROW_HEIGHT, AlertVirtualScrollDataSource } from './services/alert-virtual-scroll.data-source';
import { openConfirmationDialog } from '../../../../components/confirmation-popup/confirmation-popup';
import { PageHeaderComponent } from '../../../../components/page-header/page-header.component';
import { ErrorHandlerService } from '../../../../utils/cross-xss-script-error-handler-service';

// Dialog chrome for the End-user Preview dialog — white surface, hard corners, the spec's shadow
// and backdrop. Rules live in styles/alert-dialog.scss, global for the same reason
// CONTACT_DIALOG_CHROME's rules are (the CDK renders the surface/backdrop outside the
// component's own view) — see contacts.component.ts. The create/edit form is NOT a dialog (see
// AlertFormComponent) so it doesn't use this.
// The design's "simulated app viewport" behind the toast stack needs real screen space — a
// large viewport-sized modal, not the compact default MatDialog width.
const ALERT_DIALOG_CHROME = {
  panelClass: 'alert-dialog-panel',
  backdropClass: 'alert-dialog-backdrop',
  width: '90vw',
  maxWidth: '1200px',
  minHeight: '550px'
};

const EMPTY_RESPONSE: AlertSearchResponse = { totalCount: 0, data: [] };

@Component({
  selector: 'app-alerts',
  standalone: true,
  imports: [
    AsyncPipe,
    DatePipe,
    ReactiveFormsModule,
    AlertFiltersComponent,
    AlertFormComponent,
    AlertPreviewStackComponent,
    AlertCardComponent,
    PageHeaderComponent,
    MatNoDataRow,
    MatRecycleRows,
    MatTable,
    MatSort,
    MatSortHeader,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatCell,
    MatCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatButton,
    MatIconButton,
    MatIcon,
    MatTooltip,
    MatProgressSpinner,
    MatTabGroup,
    MatTab,
    MatTabLabel,
    ScrollingModule,
    CommonModule
  ],
  templateUrl: './alerts.component.html',
  styleUrl: './alerts.component.scss'
})
export class AlertsComponent implements OnInit {
  // A plain `@ViewChild(MatSort) matSort!: MatSort` resolves in ngAfterViewInit — too early here,
  // since the <table> (and its matSort directive) only exist in the DOM once isTableInitiating
  // flips to false, which happens asynchronously after the first search response comes back.
  // ngAfterViewInit would see `undefined` and sorting would silently never get wired up. A
  // setter (same pattern as virtualScrollViewport below) picks up MatSort whenever it actually
  // appears instead.
  // The assignment is deferred to a microtask because this setter runs mid change detection:
  // binding the sort immediately re-sorts (matSortActive="status") and swaps rows under the
  // already-checked bindings, which throws NG0100 in dev and renders one frame in the wrong
  // order in prod.
  @ViewChild(MatSort)
  set matSort(sort: MatSort | undefined) {
    if (sort && this.sortDataSource.sort !== sort) {
      queueMicrotask(() => (this.sortDataSource.sort = sort));
    }
  }

  // Desktop's scrollable list+form region and mobile's tab strip — both optional (`{ static:
  // false }` is the default) since each only exists in the DOM under its own @if branch (see
  // alerts.component.html), never both at once. Used by editAlert()/duplicateAlert() to bring
  // the always-on-page form into view when a row's Edit/Duplicate is clicked.
  @ViewChild('alertsBody') alertsBody?: ElementRef<HTMLDivElement>;
  @ViewChild('mobileTabs') mobileTabs?: MatTabGroup;

  // Mirrors ContactsComponent's own virtualScrollViewport setter — CdkTable calls
  // dataSource.connect() during its own ngOnInit, before this setter has a chance to fire (that
  // only happens in ngAfterViewInit), so the DataSource buffers the viewport itself rather than
  // assuming it's already attached (see AlertVirtualScrollDataSource's own comments).
  @ViewChild(CdkVirtualScrollViewport)
  set virtualScrollViewport(viewport: CdkVirtualScrollViewport | undefined) {
    if (viewport && !this.viewport) {
      this.viewport = viewport;
      this.dataSource.attachViewport(viewport);
      queueMicrotask(() => viewport.checkViewportSize());
    }
  }

  private viewport: CdkVirtualScrollViewport | null = null;
  readonly rowHeight = ALERT_ROW_HEIGHT;
  readonly dataSource = new AlertVirtualScrollDataSource(inject(NgZone));

  tableData: AlertSearchResponse = EMPTY_RESPONSE;
  displayedColumns: string[] = ['level', 'message', 'schedule', 'status', 'lastChanged', 'active', 'actions'];
  isLoading = true;
  isTableInitiating = true;

  // Drives the always-on-page form (see AlertFormComponent): null is the default "new alert"
  // state; set by editAlert()/duplicateAlert(), cleared by the form's own Clear button or once a
  // save completes.
  formTarget: AlertFormTarget | null = null;

  // Independent of the list's current filter/sort/search state — always the true set of
  // currently-live alerts. Backs both the red header bar's "N live alerts" count (desktop and
  // mobile) and the mobile Preview tab's card stack (see previewVisibleAlerts below) — one fetch
  // serves both, rather than the mobile tab duplicating AlertPreviewComponent's own getLive() call.
  liveAlerts: Alert[] = [];
  liveCount: number | null = null;
  previewLoading = true;
  previewHasError = false;

  // Drives the red header's "N live alerts" badge specifically — sourced from LiveAlertsService
  // (already SignalR-pushed, already fetched for the site-wide toast stack) rather than liveCount
  // above, which is only ever populated once the admin opens the End-user Preview dialog/tab and
  // stays frozen after that. This one is correct immediately on page load and stays live.
  liveAlertCount$ = inject(LiveAlertsService).liveAlertCount$;

  // Purely a client-side simulation of session dismissal for the mobile Preview tab — same
  // reasoning as AlertPreviewComponent's own dismissedIds (see that component), just scoped to
  // this page instance instead of a dialog instance.
  private previewDismissedIds = new Set<number>();

  readonly statuses = ALERT_STATUSES;
  mobileSearchControl = new FormControl('', { nonNullable: true });
  selectedMobileStatus: AlertStatus | '' = '';

  // Mobile tab order is New(0) / List(1) / Preview(2) — see the template. Always starts on New.
  selectedMobileTabIndex = 0;

  isMobile$ = inject(BreakpointObserver)
    .observe(Breakpoints.Handset)
    .pipe(map(result => result.matches));

  allAvailableColumns = [
    {
      key: 'search.message',
      name: 'Filter by message'
    }
  ];
  private alertsService = inject(AlertsService);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);
  private matSnackBar = inject(MatSnackBar);
  private errorHandler = inject(ErrorHandlerService);

  pageConfig$ = new BehaviorSubject<Partial<AlertSearchPayload>>({});

  // Sorting is done entirely client-side (DEV-1165: the API no longer accepts a sort at all —
  // there's no reason to round-trip just to reorder rows already sitting in the browser), reusing
  // Angular Material's own sort mechanism rather than a hand-written comparator: MatTableDataSource
  // sorts its `.data` against whatever MatSort it's bound to, using each mat-sort-header's id as
  // the property name to compare by default — 'level'/'showFrom'/'status'/'lastUpdatedAt' all
  // happen to match Alert's own property names exactly (see alerts.component.html), so no
  // sortingDataAccessor override is needed. Its `connect()` stream is what actually feeds the
  // virtual-scroll grid (see ngAfterViewInit) — this instance itself is never bound to a
  // <table>, it's used purely for its sorting behavior.
  private readonly sortDataSource = new MatTableDataSource<Alert>();

  constructor() {
    // Independent of when MatSort itself becomes available (see the matSort setter above) —
    // connect() reacts to sort/data changes for as long as this subscription lives, so it's safe
    // to wire up before there's any sort or any data yet.
    this.sortDataSource
      .connect()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(sorted => {
        this.dataSource.setData(sorted);
        this.viewport?.scrollToOffset(0);
      });
  }

  ngOnInit() {
    this.pageConfig$
      .pipe(
        // catchError stays inside the switchMap so a failed search only ends that one request —
        // outside it, a single error would complete pageConfig$ and every later filter/save/
        // delete refresh would silently do nothing (with isLoading stuck on).
        switchMap(config =>
          this.alertsService.search(config).pipe(
            catchError((error: HttpErrorResponse) => {
              console.error(error);
              this.isTableInitiating = false;
              this.isLoading = false;
              this.errorHandler.showApiError(
                error,
                'Could not load alerts, please try again',
                this.allAvailableColumns,
                (fieldName, options) => options.find(x => x.key === fieldName)?.name
              );
              return EMPTY;
            })
          )
        ),
        tap(response => {
          // The grid holds every matching alert in memory and virtualizes rendering client-side
          // (see AlertVirtualScrollDataSource), so a response that came back short of totalCount
          // means rows are missing from the grid entirely — not merely off a page, since there is
          // no next page to scroll to. This is exactly how usp_Alert_Search's default page size
          // going unnoticed regressed the Contacts grid once before (see its own
          // VIRTUAL_SCROLL.md) — must not be able to fail silently the same way here.
          if (response.data.length < response.totalCount) {
            console.warn(
              `Alerts grid: server returned ${response.data.length} of ${response.totalCount} alerts — ` +
                `the list is truncated. There is no paging on this endpoint (DEV-1165) — it should ` +
                `always return every matching row.`
            );
          }
          this.tableData = response;
          this.sortDataSource.data = response.data;
          this.isTableInitiating = false;
          this.isLoading = false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  onFilterChange(search: AlertSearchFilter | null) {
    this.updatePageConfig({ search });
  }

  editAlert(alert: Alert) {
    this.formTarget = { alert };

    // The always-on-page form sits above the list (desktop) or on a separate tab (mobile) — a
    // click on a row's Edit, further down the list, otherwise leaves the now-populated form
    // scrolled out of view. Desktop scrolls its shared list+form region back to top; mobile
    // switches to the New tab, where that same form lives.
    this.alertsBody?.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
    if (this.mobileTabs) {
      this.mobileTabs.selectedIndex = 0;
    }
  }

  // Duplicate inserts a copy immediately — no form step, unlike Edit. The rule (confirmed by
  // you) is that a duplicate always starts inactive, regardless of the source row's own active
  // state, so an admin must deliberately re-activate it rather than accidentally doubling up a
  // still-live alert.
  duplicateAlert(alert: Alert) {
    const data: AlertFormData = {
      level: alert.level,
      message: alert.message,
      linkText: alert.linkText,
      linkUrl: alert.linkUrl,
      visibilityMode: alert.visibilityMode,
      showFrom: alert.showFrom,
      showUntil: alert.showUntil,
      isActive: false
    };

    this.isLoading = true;
    this.alertsService
      .create(data)
      .pipe(
        tap(() => {
          this.updatePageConfig();
          this.matSnackBar.open('Alert duplicated — saved as inactive »', 'x', { panelClass: 'snackbar-green' });
        }),
        catchError((error: HttpErrorResponse) => {
          console.error(error);
          this.isLoading = false;
          this.matSnackBar.open('Could not duplicate the alert, please try again', 'x');
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  // Fired by AlertFormComponent after a successful create/update — refresh the list and return
  // the form to its default "new alert" state. The live count/preview only refetch when the
  // end-user preview is actually opened (see openPreview()/onMobileTabChange()), not on every
  // save.
  onAlertSaved() {
    this.formTarget = null;
    this.updatePageConfig();
  }

  onFormCleared() {
    this.formTarget = null;
  }

  // Inline toggle from the list row — no dialog. On failure the switch is put back to its
  // previous state and the row otherwise left alone, rather than refetching the whole list for
  // a single boolean flip.
  toggleActive(alert: Alert, event: Event) {
    const input = event.target as HTMLInputElement;
    const previous = alert.isActive;
    this.alertsService
      .setActive(alert.id, input.checked)
      .pipe(
        tap(updated => {
          alert.isActive = updated.isActive;
          alert.status = updated.status;
          alert.lastUpdatedBy = updated.lastUpdatedBy;
          alert.lastUpdatedAt = updated.lastUpdatedAt;
        }),
        catchError((error: HttpErrorResponse) => {
          console.error(error);
          input.checked = previous;
          this.matSnackBar.open('Could not update the alert, please try again', 'x');
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  deleteAlert(alert: Alert) {
    openConfirmationDialog(this.dialog, {
      title: 'Delete this alert?',
      message:
        'This removes the alert for good — users who can currently see it will lose it immediately. ' +
        'To pause it instead, switch it to inactive.',
      icon: 'warning',
      warn: true,
      confirmText: 'Delete alert'
    })
      .afterClosed()
      .pipe(
        switchMap(result => {
          if (result !== 'Confirmed') {
            return of(null);
          }
          this.isLoading = true;
          // A delete of the alert currently loaded into the form leaves stale data behind —
          // return the form to its default state rather than letting Save resurrect it.
          if (this.formTarget?.alert.id === alert.id) {
            this.formTarget = null;
          }
          return this.alertsService.delete(alert.id).pipe(
            tap(() => {
              this.updatePageConfig();
              this.matSnackBar.open('The alert was deleted successfully', 'x', { panelClass: 'snackbar-green' });
            }),
            catchError((error: HttpErrorResponse) => {
              console.error(error);
              this.isLoading = false;
              this.matSnackBar.open('Could not delete the alert, please try again', 'x');
              return of(null);
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  // Desktop end-user preview entry point. AlertPreviewComponent fetches the live alerts for its
  // own dialog card stack — this page's liveCount badge (and the mobile list's "N live"
  // description) reuse that same result via its alertsLoaded output rather than AlertsComponent
  // issuing its own separate getLive() call, which was firing the endpoint twice per click.
  openPreview() {
    const dialogRef = this.dialog.open(AlertPreviewComponent, ALERT_DIALOG_CHROME);
    dialogRef.componentInstance.alertsLoaded.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(alerts => {
      this.liveAlerts = alerts;
      this.liveCount = alerts.length;
    });
  }

  // Mobile tab strip has no dialog to "open" — the Preview tab's content is always in the DOM
  // (see alerts.component.html), so refreshLive() is triggered by selecting it instead. Index 2
  // is Preview (New=0, Alerts=1, Preview=2) — see the tab order in the template.
  private static readonly PREVIEW_TAB_INDEX = 2;

  onMobileTabChange(index: number) {
    if (index === AlertsComponent.PREVIEW_TAB_INDEX) {
      this.refreshLive();
    }
  }

  // Mobile Preview tab equivalent of the desktop dialog's card stack — the same live alerts,
  // fetched by refreshLive() when the Preview tab is selected (see onMobileTabChange()).
  get previewVisibleAlerts(): Alert[] {
    return this.liveAlerts.filter(alert => !this.previewDismissedIds.has(alert.id));
  }

  dismissPreview(id: number) {
    this.previewDismissedIds.add(id);
  }

  // Mobile's status-pill row — message search stays a plain control, but there's no level
  // filter on mobile (matches the design: message search + status pills only).
  onMobileStatusFilter(status: AlertStatus | '') {
    this.selectedMobileStatus = status;
    this.onFilterChange({ message: this.mobileSearchControl.value || null, level: null, status: status || null });
  }

  onMobileSearch() {
    this.onMobileStatusFilter(this.selectedMobileStatus);
  }

  clearMobileSearch() {
    this.mobileSearchControl.setValue('');
    this.onMobileSearch();
  }

  levelMeta(level: AlertLevel): AlertLevelMeta {
    return ALERT_LEVEL_META[level];
  }

  statusMeta(status: AlertStatus): AlertStatusMeta {
    return ALERT_STATUS_META[status];
  }

  private refreshLive() {
    this.previewLoading = true;
    this.previewHasError = false;
    this.alertsService
      .getLive()
      .pipe(
        tap(alerts => {
          this.liveAlerts = alerts;
          this.liveCount = alerts.length;
          this.previewDismissedIds.clear();
          this.previewLoading = false;
        }),
        catchError(() => {
          this.liveCount = null;
          this.previewHasError = true;
          this.previewLoading = false;
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private updatePageConfig(options?: Partial<AlertSearchPayload>) {
    this.isLoading = true;
    this.pageConfig$.next({ ...this.pageConfig$.value, ...options });
  }
}
